import { useState } from 'react';
import { Helmet } from 'react-helmet-async';

const BASE = 'https://deckprosnj.com';

/* Google Ads Tracking Template — set ONCE at account/campaign level.
 * {lpurl} = Final URL. Other {...} tokens auto-fill at click-time.
 */
const TRACKING_TEMPLATE = '{lpurl}?utm_source=google&utm_medium=cpc&utm_campaign={_campaign}&utm_content={adgroupid}&utm_term={keyword}&gclid={gclid}';

interface CountyBlock {
  county: string;
  campaign: string;
  countyPath: string;
  towns: { name: string; slug: string }[];
}

const counties: CountyBlock[] = [
  {
    county: 'Morris County',
    campaign: 'morris_county',
    countyPath: '/areas/morris-county',
    towns: [
      { name: 'Morristown', slug: 'morristown' },
      { name: 'Parsippany', slug: 'parsippany' },
      { name: 'Denville', slug: 'denville' },
      { name: 'Randolph', slug: 'randolph' },
      { name: 'Roxbury', slug: 'roxbury' },
      { name: 'Chester', slug: 'chester' },
      { name: 'Madison', slug: 'madison' },
      { name: 'Chatham', slug: 'chatham' },
      { name: 'Boonton', slug: 'boonton' },
      { name: 'Dover', slug: 'dover' },
    ],
  },
  {
    county: 'Sussex County',
    campaign: 'sussex_county',
    countyPath: '/areas/sussex-county',
    towns: [
      { name: 'Newton', slug: 'newton' },
      { name: 'Sparta', slug: 'sparta' },
      { name: 'Vernon', slug: 'vernon' },
      { name: 'Hopatcong', slug: 'hopatcong' },
      { name: 'Andover', slug: 'andover' },
      { name: 'Byram', slug: 'byram' },
      { name: 'Frankford', slug: 'frankford' },
      { name: 'Hampton', slug: 'hampton' },
      { name: 'Stillwater', slug: 'stillwater' },
      { name: 'Green', slug: 'green' },
    ],
  },
  {
    county: 'Warren County',
    campaign: 'warren_county',
    countyPath: '/areas/warren-county',
    towns: [
      { name: 'Belvidere', slug: 'belvidere' },
      { name: 'Hackettstown', slug: 'hackettstown' },
      { name: 'Phillipsburg', slug: 'phillipsburg' },
      { name: 'Washington', slug: 'washington' },
      { name: 'Blairstown', slug: 'blairstown' },
      { name: 'Knowlton', slug: 'knowlton' },
      { name: 'Oxford', slug: 'oxford' },
      { name: 'Allamuchy', slug: 'allamuchy' },
      { name: 'Independence', slug: 'independence' },
      { name: 'Lopatcong', slug: 'lopatcong' },
    ],
  },
  {
    county: 'Hunterdon County',
    campaign: 'hunterdon_county',
    countyPath: '/areas/hunterdon-county',
    towns: [
      { name: 'Flemington', slug: 'flemington' },
      { name: 'Clinton', slug: 'clinton' },
      { name: 'Lambertville', slug: 'lambertville' },
      { name: 'Raritan', slug: 'raritan' },
      { name: 'Readington', slug: 'readington' },
      { name: 'Tewksbury', slug: 'tewksbury' },
      { name: 'Bethlehem', slug: 'bethlehem' },
      { name: 'Alexandria', slug: 'alexandria' },
      { name: 'Delaware', slug: 'delaware' },
      { name: 'Union', slug: 'union' },
    ],
  },
  {
    county: 'Somerset County',
    campaign: 'somerset_county',
    countyPath: '/areas/somerset-county',
    towns: [
      { name: 'Somerville', slug: 'somerville' },
      { name: 'Bridgewater', slug: 'bridgewater' },
      { name: 'Bernardsville', slug: 'bernardsville' },
      { name: 'Bound Brook', slug: 'bound-brook' },
      { name: 'Franklin', slug: 'franklin' },
      { name: 'Hillsborough', slug: 'hillsborough' },
      { name: 'Warren', slug: 'warren' },
      { name: 'Watchung', slug: 'watchung' },
      { name: 'Bedminster', slug: 'bedminster' },
      { name: 'Branchburg', slug: 'branchburg' },
    ],
  },
];

const coreLinks = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'Offers', path: '/offers' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'Projects', path: '/projects' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
  { label: 'All Areas', path: '/areas' },
];

const AdsLinks = () => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copy = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1200);
  };

  const btnStyle = (active: boolean): React.CSSProperties => ({
    padding: '4px 10px',
    background: active ? '#a3d200' : '#f0f0f0',
    border: '1px solid #ccc',
    cursor: 'pointer',
    fontFamily: 'monospace',
    fontSize: '11px',
    minWidth: '65px',
    flexShrink: 0,
  });

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 0',
    borderBottom: '1px solid #eee',
  };

  const urlStyle: React.CSSProperties = { flex: 1, fontSize: '12px', wordBreak: 'break-all' };

  const copyCounty = (c: CountyBlock) => {
    const urls = [
      `${BASE}${c.countyPath}`,
      ...c.towns.map((t) => `${BASE}/areas/${t.slug}`),
    ].join('\n');
    copy(urls, `block-${c.campaign}`);
  };

  return (
    <div style={{ fontFamily: 'monospace', padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <Helmet>
        <title>Ads Links</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <h1 style={{ fontSize: '18px', margin: '0 0 6px' }}>Google Ads — Final URLs + Tracking</h1>
      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 20px', lineHeight: 1.5 }}>
        <strong>Step 1:</strong> Paste the Tracking Template below into Google Ads → Settings → Campaign URL options (or Account level). Change <code>{'{_campaign}'}</code> to your actual campaign name.<br />
        <strong>Step 2:</strong> Use the clean Final URLs below in each ad's <strong>Final URL</strong> field.
      </p>

      {/* Tracking Template block */}
      <div style={{ marginBottom: '28px', background: '#fffceb', border: '2px solid #ffb800', padding: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h2 style={{ fontSize: '14px', margin: 0, color: '#7a4a00' }}>📋 Tracking Template (paste once)</h2>
          <button
            onClick={() => copy(TRACKING_TEMPLATE, 'template')}
            style={{
              padding: '6px 14px',
              background: copiedKey === 'template' ? '#a3d200' : '#0B1F3A',
              color: copiedKey === 'template' ? '#0B1F3A' : '#fff',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontSize: '11px',
              fontWeight: 'bold',
            }}
          >
            {copiedKey === 'template' ? '✓ Copied Template' : 'Copy Template'}
          </button>
        </div>
        <code style={{ display: 'block', background: '#fff', padding: '10px', fontSize: '12px', wordBreak: 'break-all', border: '1px solid #e0e0e0' }}>
          {TRACKING_TEMPLATE}
        </code>
        <p style={{ fontSize: '11px', color: '#666', margin: '8px 0 0' }}>
          <strong>Note:</strong> Replace <code>{'{_campaign}'}</code> with your actual campaign name (e.g. <code>morris_county</code>) when you paste it into Google Ads.
          The other tokens (<code>{'{lpurl}'}</code>, <code>{'{adgroupid}'}</code>, <code>{'{keyword}'}</code>, <code>{'{gclid}'}</code>) are filled by Google automatically.
        </p>
      </div>

      {/* Core pages */}
      <div style={{ marginBottom: '28px' }}>
        <h2 style={{ fontSize: '14px', margin: '0 0 8px', padding: '6px 10px', background: '#0B1F3A', color: '#a3d200' }}>
          Core Pages (Final URLs)
        </h2>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {coreLinks.map((l, i) => {
            const url = `${BASE}${l.path}`;
            const key = `core-${i}`;
            return (
              <li key={l.path} style={rowStyle}>
                <span style={{ minWidth: '90px', fontSize: '11px', color: '#888', flexShrink: 0 }}>{l.label}</span>
                <span style={urlStyle}>{url}</span>
                <button onClick={() => copy(url, key)} style={btnStyle(copiedKey === key)}>
                  {copiedKey === key ? '✓ Copied' : 'Copy'}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* County blocks */}
      {counties.map((c) => (
        <div key={c.campaign} style={{ marginBottom: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#0B1F3A', color: '#a3d200', padding: '6px 10px', marginBottom: '8px' }}>
            <h2 style={{ fontSize: '14px', margin: 0 }}>
              {c.county} — campaign: <code>{c.campaign}</code>
            </h2>
            <button
              onClick={() => copyCounty(c)}
              style={{
                padding: '4px 12px',
                background: copiedKey === `block-${c.campaign}` ? '#a3d200' : '#fff',
                color: '#0B1F3A',
                border: '1px solid #a3d200',
                cursor: 'pointer',
                fontFamily: 'monospace',
                fontSize: '11px',
                fontWeight: 'bold',
              }}
            >
              {copiedKey === `block-${c.campaign}` ? '✓ All Copied' : 'Copy All in County'}
            </button>
          </div>

          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(() => {
              const url = `${BASE}${c.countyPath}`;
              const key = `${c.campaign}-county`;
              return (
                <li style={{ ...rowStyle, background: '#fffceb' }}>
                  <span style={{ minWidth: '90px', fontSize: '11px', color: '#b07a00', fontWeight: 'bold', flexShrink: 0 }}>
                    {c.county}
                  </span>
                  <span style={urlStyle}>{url}</span>
                  <button onClick={() => copy(url, key)} style={btnStyle(copiedKey === key)}>
                    {copiedKey === key ? '✓ Copied' : 'Copy'}
                  </button>
                </li>
              );
            })()}

            {c.towns.map((t) => {
              const url = `${BASE}/areas/${t.slug}`;
              const key = `${c.campaign}-${t.slug}`;
              return (
                <li key={t.slug} style={rowStyle}>
                  <span style={{ minWidth: '90px', fontSize: '11px', color: '#888', flexShrink: 0 }}>{t.name}</span>
                  <span style={urlStyle}>{url}</span>
                  <button onClick={() => copy(url, key)} style={btnStyle(copiedKey === key)}>
                    {copiedKey === key ? '✓ Copied' : 'Copy'}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '2px solid #eee', fontSize: '11px', color: '#666', lineHeight: 1.6 }}>
        <strong style={{ color: '#0B1F3A' }}>How to use in Google Ads:</strong>
        <ol style={{ paddingLeft: '20px', marginTop: '8px' }}>
          <li>Open Google Ads → <strong>Settings</strong> → <strong>Account Settings</strong> → <strong>Tracking</strong> (or Campaign → URL options).</li>
          <li>Paste the <strong>Tracking Template</strong> into the "Tracking template" field.</li>
          <li>In each Campaign, change <code>{'{_campaign}'}</code> to that campaign's name (e.g. <code>morris_county</code>).</li>
          <li>In each ad, paste the clean <strong>Final URL</strong> from the lists above into the "Final URL" field — no params needed.</li>
          <li>Google auto-builds the final clickthrough URL at click-time using the template + Final URL.</li>
        </ol>
      </div>
    </div>
  );
};

export default AdsLinks;
